import { useRef, useState } from 'react';
import { Button, Icon, Reason, Textarea, useToast } from '@efira/ui';

import { ConsoleModal } from '../modals/ConsoleModal';

/**
 * Форма обращения - ДВА КОНТЕКСТА одного окна.
 *
 * «Предложить игру» и «Сообщить о проблеме» различаются заголовком и
 * подсказкой в поле; всё остальное - текст, вложения, список своих обращений -
 * одно и то же (docs/reference/LIVECADE_UX.md, раздел 7). Делать два окна
 * значило бы поддерживать две формы ради двух строк текста.
 *
 * ОТПРАВКИ ПОКА НЕТ: серверного контура обращений в проекте нет, и выдумывать
 * его ради кнопки нельзя. Кнопка отправки живёт по правилу эталона -
 * отключена, пока поле пусто, - а при нажатии сообщает, что канал ещё не
 * подключён, вместо молчаливого проглатывания текста. Правило проекта: ни одна
 * мутация не завершается тихо.
 */
export type FeedbackKind = 'idea' | 'issue';

const COPY = {
  idea: {
    title: 'Предложить игру',
    placeholder: 'Какую игру вам хотелось бы? Опишите задумку и что в ней делают зрители.',
  },
  issue: {
    title: 'Сообщить о проблеме',
    placeholder: 'Что произошло и как это повторить?',
  },
} as const;

const LIMIT = 1000;
const MAX_FILES = 5;

export function FeedbackModal({ kind }: { kind: FeedbackKind }) {
  const copy = COPY[kind];
  const toast = useToast();
  const [text, setText] = useState('');
  const [files, setFiles] = useState<string[]>([]);
  const [reports, setReports] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  return (
    <ConsoleModal title={copy.title} dialogClassName="max-w-lg">
      {reports ? (
        <div data-feedback="reports">
          <div className="mb-3 flex items-center gap-2">
            <Button
              variant="ghost"
              size="s"
              iconOnly
              data-feedback="thread-close"
              aria-label="Закрыть список обращений"
              title="Закрыть список обращений"
              onClick={() => {
                setReports(false);
              }}
            >
              <Icon name="chevronLeft" size="sm" />
            </Button>
            <span className="text-sm font-medium">Мои обращения</span>

            {/* Закрыть список - ЯВНОЙ кнопкой, а не только стрелкой назад:
                стрелка читается как «шаг назад в истории», а человек хочет
                вернуться к форме. */}
            <Button
              variant="ghost"
              size="s"
              className="ms-auto"
              data-feedback="thread-close-text"
              onClick={() => {
                setReports(false);
              }}
            >
              Закрыть
            </Button>
          </div>
          <p className="text-foreground/80 py-6 text-center text-sm">
            Вы ещё ничего не отправляли.
          </p>
        </div>
      ) : (
        <div data-feedback="form">
          <label className="sr-only" htmlFor="feedback-text">
            {copy.title}
          </label>
          <Textarea
            id="feedback-text"
            data-feedback="textarea"
            className="min-h-28"
            placeholder={copy.placeholder}
            maxLength={LIMIT}
            value={text}
            onChange={(event) => {
              setText(event.target.value.slice(0, LIMIT));
            }}
          />
          <p className="text-foreground/80 mt-1 text-end text-sm" aria-live="polite">
            {text.length} / {LIMIT}
          </p>

          <div
            data-feedback="attach"
            className="border-foreground/15 mt-3 rounded-lg border border-dashed p-3"
          >
            <Button
              variant="secondary"
              size="s"
              className="gap-2"
              onClick={() => {
                fileInput.current?.click();
              }}
            >
              <Icon name="add" size="sm" />
              Прикрепить скриншот
            </Button>
            {/* Скрытый файловый ввод: органа управления на экране нет, его
                открывает кнопка рядом. У пресета компонента для файла нет. */}
            {/* eslint-disable-next-line no-restricted-syntax -- скрытый файловый ввод, компонента для файла у пресета нет */}
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              multiple
              className="hidden"
              onChange={(event) => {
                const picked = [...(event.target.files ?? [])].map((file) => file.name);
                setFiles((current) => [...current, ...picked].slice(0, MAX_FILES));
              }}
            />
            <p className="text-foreground/80 mt-2 text-sm">
              PNG, JPG, GIF, WebP до 10 МБ. Осталось {MAX_FILES - files.length} из {MAX_FILES}.
            </p>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((name) => (
                  <li key={name} className="text-foreground/80 truncate text-xs">
                    {name}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Button
              variant="ghost"
              size="s"
              data-feedback="my-reports"
              onClick={() => {
                setReports(true);
              }}
            >
              Мои обращения
            </Button>
            <span className="ms-auto flex gap-2">
              <Button
                variant="secondary"
                size="s"
                data-feedback="cancel"
                onClick={() => {
                  setText('');
                  history.back();
                }}
              >
                Отмена
              </Button>
              <Button
                variant="primary"
                size="s"
                data-feedback="send"
                disabled={text.trim() === ''}
                aria-describedby={text.trim() === '' ? 'feedback-why' : undefined}
                onClick={() => {
                  toast.show({
                    tone: 'warning',
                    title: 'Приём обращений ещё не подключён',
                    text: 'Напишите в Discord — ссылка в левой колонке. Текст не потерян, он остался в поле.',
                  });
                }}
              >
                Отправить
              </Button>
            </span>
          </div>
          {text.trim() === '' && (
            <Reason id="feedback-why" className="text-end">
              Напишите текст — пустое обращение отправлять нечего.
            </Reason>
          )}
        </div>
      )}
    </ConsoleModal>
  );
}
